// src/frontend/js/modern/modules/multi-list/actions.js

import { BaseModule } from '../../shared/module-base.js';
import { eventBus, EVENTS } from '../../core/event-bus.js';
import { queueManager } from '../../core/queue.js';
import multiListModuleStore from './store.js';
import multiListUI from './ui.js';
import { showError, block, unblock } from '../../shared/ui-helpers.js';
import { loadAutomations } from '../../shared/api-helpers.js';
import { ScopeHelper } from '../../shared/scope-helpers.js';
import { displayStore } from '../../core/storage/display-store.js';
import { hidePopup } from '../../lib/popup-lite.js';
import { stateCache } from '../../core/state-cache.js';
import { fragmentManager } from '../../core/fragment-manager.js';

class MultiListActions extends BaseModule {
	constructor() {
		super('multiList', multiListModuleStore, null, multiListUI);
		this._listCreationPromises = new Map();
	}

	waitForListCreation(tempId) {
		const entry = this._listCreationPromises.get(tempId);
		if (!entry) {
			const list = this.store.getList(tempId);
			if (list && !this.store.isPendingList(list.wishlist_id)) {
				return Promise.resolve(list.wishlist_id);
			}
			return Promise.resolve(tempId);
		}
		return entry.promise;
	}

	_closeParentPopup(element) {
		if (!element) return;
		const popup = element.closest('.wlfmc-popup');
		if (popup) {
			hidePopup(popup);
		}
	}

	_injectListIntoOpenPopups(listData, getIsOnList = null) {
		['add_to_list_popup', 'move_popup', 'copy_popup'].forEach((popupId) => {
			const popupEl = document.getElementById(popupId);
			if (!popupEl) return;
			const isVisible = popupEl.closest('.popup_wrapper_visible') !== null
				|| popupEl.closest('.popup_wrapper')?.classList.contains('popup_wrapper_visible')
				|| document.documentElement.classList.contains(`popup_visible_${popupId}`);
			if (!isVisible) return;
			const listContainer = popupEl.querySelector('ul.list');
			if (!listContainer) return;
			if (listContainer.querySelector(`[data-wishlist-id="${listData.wishlist_id}"]`)) return;
			const template = window.wp?.template?.('wlfmc-list-item');
			if (!template) return;
			let isOnList = false;
			if (typeof getIsOnList === 'function') {
				isOnList = getIsOnList(popupId);
			} else if (typeof getIsOnList === 'boolean') {
				isOnList = getIsOnList;
			}
			const html = template({
				wishlist_id: listData.wishlist_id,
				wishlist_name: listData.wishlist_name,
				wishlist_desc: listData.wishlist_desc || '',
				wishlist_image: listData.wishlist_image || '',
				wishlist_url: '#',
				is_on_list: isOnList,
			});
			listContainer.insertAdjacentHTML('beforeend', html);
			const emptyMsg = popupEl.querySelector('.list-empty');
			if (emptyMsg) emptyMsg.style.display = 'none';
			const noResults = popupEl.querySelector('.no-results-row');
			if (noResults) noResults.style.display = 'none';
			const saveBtn = popupEl.querySelector('.wlfmc_add_to_multi_list');
			if (saveBtn) saveBtn.style.display = '';
		});
	}

	async addFromHandler(productId, formData, options = {}) {
		const { wishlistIds = [], currentLists = [], cartItemKey = '' } = options;
		const actionId = `multi_sync_${productId}_${Date.now()}`;
		const toAdd = wishlistIds.filter(id => !currentLists.map(String).includes(String(id)));
		const toRemove = currentLists.filter(id => !wishlistIds.map(String).includes(String(id)));
		const scope = ScopeHelper.multiListBatch(productId);
		// Cart item key mode: no optimistic updates, loader stays until server responds
		const isCartMode = !!cartItemKey;

		return this.executeWithConflictManagement(
			scope,
			actionId,
			isCartMode ? null : () => {
				if (toAdd.length) this._optimisticAdd(productId, toAdd);
				if (toRemove.length) this._optimisticRemove(productId, toRemove);
				if (!toAdd.length && !toRemove.length) return;
				// Counter/list rendering is handled reactively by renderFromState().
				this._closeParentPopup(options?.element);
			},
			isCartMode ? null : () => {
				if (toAdd.length) this._optimisticRemove(productId, toAdd);
				if (toRemove.length) this._optimisticAdd(productId, toRemove);
				// Counter/list rendering is handled reactively by renderFromState().
			},
			() => {
				const tempIds = wishlistIds.filter(id => this.store.isPendingList(id));
				if (tempIds.length > 0) {
					return Promise.all(tempIds.map(id => this.waitForListCreation(id)))
						.then(realIds => {
							const resolvedIds = wishlistIds.map(id => {
								const idx = tempIds.indexOf(id);
								return idx >= 0 ? realIds[idx] : id;
							});
							formData.delete('wishlist_ids');
							formData.append('wishlist_ids', JSON.stringify(resolvedIds));
							if (formData.has('current_lists')) {
								try {
									const cl = JSON.parse(formData.get('current_lists'));
									const resolvedCL = cl.map(id => {
										const idx = tempIds.indexOf(id);
										return idx >= 0 ? realIds[idx] : id;
									});
									formData.delete('current_lists');
									formData.append('current_lists', JSON.stringify(resolvedCL));
								} catch (e) { /* keep original */ }
							}
							return queueManager.addWithDeduplication('add_to_list', formData);
						});
				}
				return queueManager.addWithDeduplication('add_to_list', formData);
			},
			(result) => {
				const prod_id = productId || result.product_id;
				this._handleSyncSuccess(prod_id, toAdd, toRemove, result);
				// In cart mode, close the popup only after server confirmation
				if (isCartMode) {
					this._closeParentPopup(options?.element);
				}
			},
			(error) => this._handleAddError(productId, toAdd.length ? toAdd : toRemove, error),
			{ type: 'add', productId, element: options?.element }
		);
	}

	async removeFromList(productId, listId, nonce = '', element = null, wishlistToken = '') {
		const actionId = `multi_remove_${productId}_${listId}_${Date.now()}`;
		const scope = ScopeHelper.multiList(productId, listId);

		const requestData = {
			product_id: productId,
			remove_from_wishlist: productId,
			wishlist_id: listId,
			nonce,
			_scope: scope,
		};

		// Shared lists may require the table token for server-side validation.
		if (wishlistToken) {
			requestData.wishlist_token = wishlistToken;
		}

		return this.executeWithConflictManagement(
			scope,
			actionId,
			() => {
				this._optimisticRemove(productId, [listId]);
				this._closeParentPopup(element);
			},
			() => this._optimisticAdd(productId, [listId]),
			() => queueManager.addWithDeduplication('remove_from_list', requestData),
			(result) => {
				this.ui.updateRemoveButton(productId, listId);

				// Reload table fragment from server to remove the disabled row entirely.
				fragmentManager.loadFragments();

				// Counter/list rendering is handled reactively by renderFromState().
				// In merge mode, wishlist counter updates are triggered automatically
				// by StateCache notifications inside removeItemFromList().
				eventBus.emit(EVENTS.MULTI_LIST_REMOVED, { productId, listId, status: 'success', data: result });
			},
			(error) => {
				showError(error.message || window.wlfmc_l10n?.labels?.multi_list_add_failed);
				eventBus.emit(EVENTS.MULTI_LIST_REMOVED, { productId, listId, status: 'failed', error: error.message });
			},
			{ type: 'remove', productId, listId, element: element }
		);
	}

	async createFromHandler(name, description, privacy, nonce, element = null) {
		const actionId = `multi_create_${Date.now()}`;
		const tempId = 'temp_' + Date.now();
		this._lastTempId = tempId;
		const scope = ScopeHelper.multiListManage('create', tempId);

		let resolveCreation, rejectCreation;
		const creationPromise = new Promise((resolve, reject) => {
			resolveCreation = resolve;
			rejectCreation = reject;
		});
		this._listCreationPromises.set(tempId, {
			promise: creationPromise,
			resolve: resolveCreation,
			reject: rejectCreation,
		});

		return this.executeWithConflictManagement(
			scope,
			actionId,
			() => {
				this._optimisticCreate(name, description, privacy, tempId);
				this._closeParentPopup(element);
			},
			() => {
				this._optimisticDelete(tempId);
				const entry = this._listCreationPromises.get(tempId);
				if (entry) {
					entry.reject(new Error('List creation rolled back'));
					this._listCreationPromises.delete(tempId);
				}
			},
			() => queueManager.addWithDeduplication('create_list', {
				list_name: name,
				list_descriptions: description,
				list_privacy: privacy,
				nonce: nonce,
				_scope: scope,
			}),
			(result) => this._handleCreateSuccess(result, tempId),
			(error) => this._handleCreateError(error),
			{ type: 'create', listId: tempId, element: element }
		);
	}

	async deleteFromHandler(listId, nonce, element = null) {
		const actionId = `multi_delete_${listId}_${Date.now()}`;
		const scope = ScopeHelper.multiListManage('delete', listId);

		return this.executeWithConflictManagement(
			scope,
			actionId,
			() => {
				this._optimisticDelete(listId);
				this._closeParentPopup(element);
			},
			() => { /* Handled by sync on failure */ },
			() => queueManager.addWithDeduplication('delete_list', {
				wishlist_id: listId,
				nonce: nonce,
				_scope: scope,
			}),
			(result) => this._handleDeleteSuccess(listId, result),
			(error) => this._handleDeleteError(listId, error),
			{ type: 'delete', listId, element: element }
		);
	}

	async changePrivacyFromHandler(listId, newPrivacy, currentPrivacy, nonce, element) {
		const actionId = `multi_privacy_${listId}_${Date.now()}`;
		const scope = ScopeHelper.multiListManage('privacy', listId);

		return this.executeWithConflictManagement(
			scope,
			actionId,
			() => this._optimisticPrivacy(listId, newPrivacy),
			() => this._optimisticPrivacy(listId, currentPrivacy),
			() => queueManager.addWithDeduplication('change_privacy', {
				wishlist_id: listId,
				list_privacy: newPrivacy,
				nonce: nonce,
				_scope: scope,
			}),
			(result) => this._handlePrivacySuccess(listId, newPrivacy, result),
			(error) => this._handlePrivacyError(listId, currentPrivacy, error),
			{ type: 'update', listId, element: element }
		);
	}

	async updateFromHandler(listId, name, description, privacy, nonce, element = null) {
		const actionId = `multi_update_${listId}_${Date.now()}`;
		const scope = ScopeHelper.multiListManage('update', listId);
		const oldData = this._getListData(listId);

		return this.executeWithConflictManagement(
			scope,
			actionId,
			() => {
				this._optimisticUpdate(listId, name, description, privacy);
				this._closeParentPopup(element);
			},
			() => {
				if (oldData) {
					this._optimisticUpdate(listId, oldData.wishlist_name, oldData.wishlist_desc, oldData.wishlist_privacy);
				}
			},
			() => queueManager.addWithDeduplication('update_list', {
				wishlist_id: listId,
				list_name: name,
				list_descriptions: description,
				list_privacy: privacy,
				nonce: nonce,
				_scope: scope,
			}),
			(result) => this._handleUpdateSuccess(listId, result, { name, description, privacy }),
			(error) => this._handleUpdateError(listId, error),
			{ type: 'update', listId, element: element }
		);
	}

	/* ═══════════════════════════════════════════
	   OPTIMISTIC — State mutations ONLY.
	   All counter/list UI reconciliation is handled
	   reactively by MultiListUI.renderFromState().
	════════════════════════════════════════════ */

	_optimisticAdd(productId, listIds) {
		for (const listId of listIds) {
			this.store.addItemToList(listId, productId);
		}
		this.ui.updateAddButton(productId, listIds);
		// In merge mode, wishlist counter updates are triggered automatically
		// by StateCache._notify('wishlist', 'multi-list-changed') inside
		// addItemToList(). No manual bridge needed.
	}

	_optimisticRemove(productId, listIds) {
		if (!Array.isArray(listIds)) return;
		for (const listId of listIds) {
			this.store.removeItemFromList(listId, productId);
		}
		if (this.store.getProductListCount(productId) === 0) {
			displayStore.remove('multiList', productId);
		}
		this.ui.updateAddButton(productId, []);
		// In merge mode, wishlist counter updates are triggered automatically
		// by StateCache._notify('wishlist', 'multi-list-changed') inside
		// removeItemFromList(). No manual bridge needed.
	}

	_optimisticCreate(name, description, privacy, tempId) {
		const list = this.store.addList({
			wishlist_id: tempId,
			wishlist_name: name,
			wishlist_desc: description,
			wishlist_privacy: privacy,
			wishlist_items: [],
			_optimistic: true,
		});
		// Render the new list into the manage-lists page (pending state).
		this.ui.updateListUI({
			wishlist_id: tempId,
			wishlist_name: name,
			wishlist_desc: description,
			wishlist_privacy: privacy,
			wishlist_items: [],
			pending: true,
		});
		// Inject into any currently open popups (add_to_list, move, copy).
		this._injectListIntoOpenPopups({
			wishlist_id: tempId,
			wishlist_name: name,
			wishlist_desc: description,
			wishlist_privacy: privacy,
		});
		['add_to_list_popup', 'move_popup', 'copy_popup'].forEach((popupId) => {
			const popupEl = document.getElementById(popupId);
			if (!popupEl) return;
			// 1) Show the save button (hidden when there were zero lists)
			const saveBtn = popupEl.querySelector('.wlfmc_add_to_multi_list');
			if (saveBtn) saveBtn.style.display = '';
			// 2) Hide empty / no-results messages
			const emptyMsg = popupEl.querySelector('.list-empty');
			if (emptyMsg) emptyMsg.style.display = 'none';
			const noResults = popupEl.querySelector('.no-results-row');
			if (noResults) noResults.style.display = 'none';
			// 3) Check the checkbox optimistically (DOM only, no state change)
			const checkbox = popupEl.querySelector(
				`input.list-item-checkbox[value="${tempId}"]`
			);
			if (checkbox) {
				checkbox.checked = true;
				checkbox.dispatchEvent(new Event('change', { bubbles: true }));
			}
			if (!Array.isArray(popupEl._wlfmcPrevStates)) {
				popupEl._wlfmcPrevStates = [];
			}
			popupEl._wlfmcPrevStates.push(false);
		});
		// Counter/list rendering is handled reactively by renderFromState().
	}

	_optimisticDelete(listId) {
		const list = this.store.getList(listId);
		this._lastDeletedList = list ? { ...list } : null;
		this.store.removeList(listId);
		this.ui.removeListUI(listId);
		// Counter/list rendering is handled reactively by renderFromState().
	}

	_optimisticUpdate(listId, name, description, privacy) {
		this.store.updateList(listId, {
			wishlist_name: name,
			wishlist_desc: description,
			wishlist_privacy: privacy,
		});
		this.ui.refreshListDetails(listId, {
			wishlist_id: listId,
			wishlist_name: name,
			wishlist_desc: description,
			wishlist_privacy: privacy,
		});
	}

	_optimisticPrivacy(listId, privacy) {
		this.store.updateList(listId, { wishlist_privacy: privacy });
		this.ui.updatePrivacyUI(listId, privacy);
	}
	/* ═══════════════════════════════════════════
	   SUCCESS HANDLERS — Confirm state + side effects.
	   No updateCounter / syncListCounterRows /
	   updateListItemCounts / updateProductListCount calls.
	   Reactive rendering handles all UI reconciliation.
	════════════════════════════════════════════ */
	/* ═══════════════════════════════════════════
   SUCCESS HANDLERS — Confirm state + side effects.
   _syncMergedWishlist has been REMOVED.
   Wishlist counter reconciliation in merge mode is now
   fully reactive via StateCache notifications triggered
   by addItemToList / removeItemFromList.
	════════════════════════════════════════════ */

	_handleSyncSuccess(productId, toAdd, toRemove, result) {
		if (result.display) {
			displayStore.set('multiList', productId, result.display);
		}
		for (const listId of toAdd) {
			this.store.confirmAddItem(listId, productId, result);
		}
		for (const listId of toRemove) {
			this.store.removeItemFromList(listId, productId);
		}
		// Counter/list rendering is handled reactively by renderFromState().
		// In merge mode, wishlist counter is updated automatically via
		// StateCache notifications triggered by addItemToList/removeItemFromList.
		// The old _syncMergedWishlist bridge has been REMOVED because it caused
		// race conditions by rewriting the entire wishlist items array.
		if (toAdd.length > 0 && result.customer_id) {
			const listType = (result.default_wishlist_id && result.default_wishlist_id !== 'false')
				? 'wishlist' : 'lists';
			const automationWishlistId = (result.default_wishlist_id && result.default_wishlist_id !== 'false')
				? result.default_wishlist_id : result.last_list_id;
			if (automationWishlistId) {
				loadAutomations(productId, automationWishlistId, result.customer_id, listType, result.load_automation_nonce);
			}
		}

		eventBus.emit(EVENTS.MULTI_LIST_ADDED, {
			productId,
			listIds: toAdd,
			removedListIds: toRemove,
			status: 'success',
			data: result,
		});
	}

	_handleAddSuccess(productId, listIds, result) {
		if (result.display) {
			displayStore.set('multiList', productId, result.display);
		}
		for (const listId of listIds) {
			this.store.confirmAddItem(listId, productId, result);
		}
		this.ui.updateAddButton(productId, listIds, result);
		// Counter/list rendering is handled reactively by renderFromState().
		// In merge mode, wishlist counter is updated automatically via
		// StateCache notifications. No manual bridge needed.
		if (result.customer_id) {
			const listType = result.default_wishlist_id && result.default_wishlist_id !== 'false'
				? 'wishlist' : 'lists';
			const automationWishlistId = result.default_wishlist_id && result.default_wishlist_id !== 'false'
				? result.default_wishlist_id : result.last_list_id;
			if (automationWishlistId) {
				loadAutomations(productId, automationWishlistId, result.customer_id, listType, result.load_automation_nonce);
			}
		}

		eventBus.emit(EVENTS.MULTI_LIST_ADDED, { productId, listIds, status: 'success', data: result });
	}

	_handleAddError(productId, listIds, error) {
		// error.message carries the server-translated message from the response.
		showError(error.message || window.wlfmc_l10n?.labels?.multi_list_add_failed);
		eventBus.emit(EVENTS.MULTI_LIST_ADDED, { productId, listIds, status: 'failed', error: error.message });
	}

	_handleCreateSuccess(result, tempId) {
		if (result.wishlist_id) {
			const entry = this._listCreationPromises.get(tempId);
			if (entry) {
				entry.resolve(result.wishlist_id);
				this._listCreationPromises.delete(tempId);
			}
			// Morph existing temp elements in place to preserve checkbox checked state.
			document.querySelectorAll(`[data-wishlist-id="${tempId}"]`).forEach(el => {
				el.setAttribute('data-wishlist-id', result.wishlist_id);
				const cb = el.querySelector(`input.list-item-checkbox[value="${tempId}"]`)
					|| el.querySelector(`input[type="checkbox"][value="${tempId}"]`);
				if (cb) cb.value = result.wishlist_id;
				el.classList.remove('wlfmc-pending');
			});
			document.querySelectorAll(`[data-list-id="${tempId}"]`).forEach(el => {
				el.setAttribute('data-list-id', result.wishlist_id);
			});
			// Rename popup elements themselves.
			const editPopup = document.getElementById(`edit_popup_${tempId}`);
			if (editPopup) editPopup.id = `edit_popup_${result.wishlist_id}`;
			const deletePopup = document.getElementById(`delete_popup_${tempId}`);
			if (deletePopup) deletePopup.id = `delete_popup_${result.wishlist_id}`;
			const sharePopup = document.getElementById(`share_popup_${tempId}`);
			if (sharePopup) sharePopup.id = `share_popup_${result.wishlist_id}`;
			// Rename popup TRIGGER buttons (data-popup-id references).
			document.querySelectorAll(`[data-popup-id="edit_popup_${tempId}"]`).forEach(el => {
				el.setAttribute('data-popup-id', `edit_popup_${result.wishlist_id}`);
			});
			document.querySelectorAll(`[data-popup-id="delete_popup_${tempId}"]`).forEach(el => {
				el.setAttribute('data-popup-id', `delete_popup_${result.wishlist_id}`);
			});
			document.querySelectorAll(`[data-popup-id="share_popup_${tempId}"]`).forEach(el => {
				el.setAttribute('data-popup-id', `share_popup_${result.wishlist_id}`);
			});
			const wishlistUrl = result.wishlist_url || result.url || '';
			if (tempId) {
				this.store.updateList(tempId, {
					wishlist_id: result.wishlist_id,
					wishlist_name: result.wishlist_name || result.list_name || '',
					wishlist_desc: result.wishlist_desc || result.list_descriptions || '',
					wishlist_privacy: result.wishlist_privacy || 0,
					wishlist_url: wishlistUrl,
					wishlist_token: result.wishlist_token || '',
					_optimistic: false,
				});
			}
			// Resolve nonces: prefer server response, fall back to page-level nonce.
			const pageNonce = document.querySelector('#wlfmc-lists')?.dataset.nonce || '';
			const listsNonce = result.lists_nonce || result.nonce || pageNonce;
			const deleteNonce = result.delete_nonce || pageNonce;
			const privacyNonce = result.privacy_nonce || pageNonce;
			this.ui.updateListUI({
				wishlist_id: result.wishlist_id,
				wishlist_name: result.wishlist_name || result.list_name || '',
				wishlist_desc: result.wishlist_desc || result.list_descriptions || '',
				wishlist_privacy: result.wishlist_privacy || 0,
				wishlist_token: result.wishlist_token || '',
				wishlist_url: wishlistUrl,
				wishlist_count: 0,
				pending: false,
				lists_nonce: listsNonce,
				delete_nonce: deleteNonce,
				privacy_nonce: privacyNonce,
			});
			// Fallback: inject into popups that were opened AFTER the optimistic
			// create (they would not have a temp element to morph).
			this._injectListIntoOpenPopups({
				wishlist_id: result.wishlist_id,
				wishlist_name: result.wishlist_name || result.list_name || '',
				wishlist_desc: result.wishlist_desc || result.list_descriptions || '',
				wishlist_privacy: result.wishlist_privacy || 0,
			}, false);
			document.querySelectorAll('.wlfmc-list-actions-wrapper .wlfmc-new-list').forEach(el => el.style.display = 'none');
			eventBus.emit(EVENTS.MULTI_LIST_CREATED, { listId: result.wishlist_id, status: 'success', data: result });
		}
	}

	_handleCreateError(error) {
		if (this._lastTempId) {
			const entry = this._listCreationPromises.get(this._lastTempId);
			if (entry) {
				entry.reject(error);
				this._listCreationPromises.delete(this._lastTempId);
			}
			this._optimisticDelete(this._lastTempId);
			this._lastTempId = null;
		}
		// error.message carries the server-translated message from the response.
		showError(error.message || window.wlfmc_l10n?.labels?.multi_list_create_failed);
		eventBus.emit(EVENTS.MULTI_LIST_CREATED, { status: 'failed', error: error.message });
	}

	_handleUpdateSuccess(listId, result, sentData) {
		const name = result?.wishlist_name || result?.list_name || sentData?.name || '';
		const desc = result?.wishlist_desc || result?.list_descriptions || sentData?.description || '';
		const privacy = result?.wishlist_privacy !== undefined
			? Number(result.wishlist_privacy)
			: (sentData?.privacy ?? 0);
		this.store.updateList(listId, {
			wishlist_name: name,
			wishlist_desc: desc,
			wishlist_privacy: privacy,
		});
		this.ui.refreshListDetails(listId, {
			wishlist_name: name,
			wishlist_desc: desc,
			wishlist_privacy: privacy,
		});
		eventBus.emit(EVENTS.MULTI_LIST_UPDATED, { listId, status: 'success', data: result });
	}

	_handleUpdateError(listId, error) {
		// error.message carries the server-translated message from the response.
		showError(error.message || window.wlfmc_l10n?.labels?.multi_list_update_failed);
		eventBus.emit(EVENTS.MULTI_LIST_UPDATED, { listId, status: 'failed', error: error.message });
	}

	_handleDeleteSuccess(listId, result) {
		this.ui.removeListUI(listId);
		eventBus.emit(EVENTS.MULTI_LIST_DELETED, { listId, status: 'success', data: result });
	}

	_handleDeleteError(listId, error) {
		// error.message carries the server-translated message from the response.
		showError(error.message || window.wlfmc_l10n?.labels?.multi_list_delete_failed);
		eventBus.emit(EVENTS.MULTI_LIST_DELETED, { listId, status: 'failed', error: error.message });
	}

	_handlePrivacySuccess(listId, privacy, result) {
		this.ui.updatePrivacyUI(listId, privacy);
		eventBus.emit(EVENTS.MULTI_LIST_PRIVACY_CHANGED, { listId, privacy, status: 'success', data: result });
	}

	_handlePrivacyError(listId, oldPrivacy, error) {
		// error.message carries the server-translated message from the response.
		showError(error.message || window.wlfmc_l10n?.labels?.multi_list_privacy_failed);
		eventBus.emit(EVENTS.MULTI_LIST_PRIVACY_CHANGED, { listId, privacy: oldPrivacy, status: 'failed', error: error.message });
	}

	_getListData(listId) {
		return this.store.getList(listId);
	}

	/**
	 * Load user lists with context-aware behavior.
	 *
	 * Three operational modes:
	 * 1. cartItemKey + popup → Always fetch from server (bypass cache), block popup UI
	 * 2. productId + popup   → Cache-first; fall back to server if cache is empty
	 * 3. No popup context    → Cache-first; fall back to server without product context
	 *
	 * @param {object}              options
	 * @param {number|string|null}  options.productId
	 * @param {string|null}         options.cartItemKey
	 * @param {string|boolean|null} options.excludeDefault
	 * @param {HTMLElement|null}    options.popup
	 * @returns {Promise<Array>}
	 */
	async loadLists({ productId = null, cartItemKey = null, excludeDefault = null, popup = null } = {}) {
		const isMerge = window.wlfmc_l10n?.merge_lists === 'true';
		if (excludeDefault === null) {
			excludeDefault = isMerge ? 'false' : 'true';
		}
		// Mode 1: Cart item key + popup → always fetch from server with blocking UI
		if (cartItemKey && popup) {
			return this._loadListsForPopup({ popup, excludeDefault, cartItemKey, productId });
		}
		// Mode 2: Product ID + popup → cache-first, then server
		if (productId && popup) {
			const cachedLists = stateCache.getLists();
			if (cachedLists && cachedLists.length > 0) {
				const lists = this.store.getUserLists(cachedLists, excludeDefault);
				const productLists = multiListUI.getProductListsFromCache(productId);
				multiListUI.renderMultiListPopup(popup, lists, productLists);
				return lists;
			}
			return this._loadListsForPopup({ popup, excludeDefault, productId });
		}
		// Mode 3: No popup context → cache-first, then server without product context
		const cachedLists = stateCache.getLists();
		if (cachedLists && cachedLists.length > 0) {
			return this.store.getUserLists(cachedLists, excludeDefault);
		}
		return this._fetchListsFromServer({ excludeDefault });
	}
	/**
	 * Fetch lists from server and render them into the popup.
	 * Blocks the popup container during the request lifecycle.
	 *
	 * @param {object}              options
	 * @param {HTMLElement}         options.popup
	 * @param {string|boolean}      options.excludeDefault
	 * @param {number|string|null}  options.productId
	 * @param {string|null}         options.cartItemKey
	 * @returns {Promise<Array>}
	 */
	async _loadListsForPopup({ popup, excludeDefault, productId = null, cartItemKey = null }) {
		const container = popup.querySelector('.wlfmc-add-to-list-container');
		if (container) {
			block(container);
		}
		try {
			const { lists, productLists, error } = await this._fetchListsFromServer({
				excludeDefault,
				productId,
				cartItemKey,
				raw: true,
			});
			// Server returned an error message — show toast and close popup
			if (error) {
				showError(error);
				hidePopup(popup);
				return [];
			}
			multiListUI.renderMultiListPopup(popup, lists, productLists);
			return lists;
		} finally {
			if (container) {
				unblock(container);
			}
		}
	}
	/**
	 * Core server fetch for lists.
	 *
	 * @param {object}              options
	 * @param {string|boolean|null} options.excludeDefault
	 * @param {number|string|null}  options.productId
	 * @param {string|null}         options.cartItemKey
	 * @param {boolean}             options.raw - Return full response shape { lists, productLists, error }
	 * @returns {Promise<Array|{lists: Array, productLists: Array, error: string|null}>}
	 */
	async _fetchListsFromServer({ excludeDefault, productId = null, cartItemKey = null, raw = false }) {
		const l10n = window.wlfmc_l10n || {};
		const ajaxUrl = l10n.multi_list_ajax_url || l10n.ajax_url;
		const actionName = (l10n.actions && l10n.actions.load_lists_action) || 'wlfmc_load_lists';
		const params = {
			action: actionName,
			nonce: l10n.nonce || '',
			context: 'frontend',
			exclude_default: excludeDefault,
		};
		if (productId) {
			params.product_id = String(productId);
		}
		if (cartItemKey) {
			params.cart_item_key = cartItemKey;
		}
		const headers = { 'Content-Type': 'application/x-www-form-urlencoded' };
		if (l10n.ajax_mode === 'rest_api' && l10n.nonce) {
			headers['X-WP-Nonce'] = l10n.nonce;
		}
		try {
			const response = await fetch(ajaxUrl, {
				method: 'POST',
				headers,
				body: new URLSearchParams(params),
			});
			const data = await response.json();
			if (data.result === 'true' && data.data) {
				const lists = Array.isArray(data.data) ? data.data : [];
				const productLists = Array.isArray(data.product_lists) ? data.product_lists : [];
				// Sync fetched lists into the store
				this.store.syncFromData({ lists, count: lists.length });
				if (raw) {
					return { lists: this.store.getUserLists(lists, excludeDefault), productLists, error: null };
				}
				return this.store.getUserLists(lists, excludeDefault);
			}
			// Server returned a non-success result
			const errorMessage = data.message || null;
			return raw ? { lists: [], productLists: [], error: errorMessage } : [];
		} catch (error) {
			console.warn('[MultiList] Load lists failed:', error);
			return raw ? { lists: [], productLists: [], error: null } : [];
		}
	}
}

export const multiListActions = new MultiListActions();
export default multiListActions;
